/**
 * Tiny key-value persistence interface for the launcher's runtime
 * subsystems (remote-config last-known-good, telemetry overflow ring,
 * potentially device-local theme preference).
 *
 * Three implementations:
 *   - `LocalStorageKeyValueStore` — production browser / WebView build;
 *     wraps `window.localStorage`. Synchronous reads/writes, ~5–10MB
 *     budget on Android WebView, persists across reboots.
 *   - `InMemoryKeyValueStore` — test fake. Same surface, deterministic.
 *   - `NullKeyValueStore` — opt-out; reads always return null, writes
 *     are no-ops. Used by callers that don't want persistence (e.g.,
 *     the demo / preview iframe where each pageview should start clean).
 *
 * Methods are intentionally minimal. Subsystems wrap their own
 * (de)serialization so the store stays untyped — string in, string
 * out. Callers handle JSON.parse / JSON.stringify themselves and are
 * expected to tolerate corrupt or missing values gracefully (return
 * the in-memory default, don't crash).
 *
 * All methods are sync. The Android WebView's localStorage is sync
 * already, and the launcher's reboot-safe write points (telemetry
 * stop(), remote-config onConfig) all happen at moments where blocking
 * for a few ms is fine.
 */
export interface KeyValueStore {
  /** Returns the stored string, or `null` when absent / unreadable. */
  get(key: string): string | null;
  /** Persists the string. Implementations may silently fail (quota,
   *  private-browsing) — that's a logging concern, not a fatal one. */
  set(key: string, value: string): void;
  /** Removes the key. No-op when absent. */
  remove(key: string): void;
}

/** Production browser / Android WebView store. Falls back to a private
 *  in-memory map if `window.localStorage` is unavailable (private mode,
 *  sandboxed iframe) so callers can still construct without guarding. */
export class LocalStorageKeyValueStore implements KeyValueStore {
  private readonly fallback = new Map<string, string>();
  private readonly storage: Storage | null;

  constructor() {
    try {
      // Probe — some private modes throw on access, not just set.
      const test = '__ml_kv_probe__';
      window.localStorage.setItem(test, '1');
      window.localStorage.removeItem(test);
      this.storage = window.localStorage;
    } catch {
      this.storage = null;
    }
  }

  get(key: string): string | null {
    if (this.storage === null) return this.fallback.get(key) ?? null;
    try {
      return this.storage.getItem(key);
    } catch {
      return this.fallback.get(key) ?? null;
    }
  }

  set(key: string, value: string): void {
    if (this.storage === null) {
      this.fallback.set(key, value);
      return;
    }
    try {
      this.storage.setItem(key, value);
    } catch {
      // Quota exceeded, etc. — keep the in-memory copy at least so
      // the current session has access; can't promise reboot survival.
      this.fallback.set(key, value);
    }
  }

  remove(key: string): void {
    this.fallback.delete(key);
    if (this.storage === null) return;
    try {
      this.storage.removeItem(key);
    } catch {
      // No-op
    }
  }
}

/** Deterministic in-memory store for tests + any caller that wants
 *  per-session persistence without touching disk. */
export class InMemoryKeyValueStore implements KeyValueStore {
  private readonly map = new Map<string, string>();
  get(key: string): string | null {
    return this.map.get(key) ?? null;
  }
  set(key: string, value: string): void {
    this.map.set(key, value);
  }
  remove(key: string): void {
    this.map.delete(key);
  }
}

/** Opt-out — reads null, writes are no-ops. Useful when a subsystem
 *  takes an optional store and the caller wants to disable persistence. */
export const NULL_KEY_VALUE_STORE: KeyValueStore = {
  get: () => null,
  set: () => undefined,
  remove: () => undefined,
};
